/**
 * The FrameModel builder: what the scene holds, laid out and animated, packed
 * into the typed arrays both renderers draw.
 *
 * Every visible row (a position of a World, or one plane's lane under it)
 * owns a fixed run of bit instances. A row is repacked only when it changes:
 * its signature (geometry, the shown masks, hues, facets) moves, a flip or a
 * tween touches it, or the theme changes. The ranges repacked since the last
 * frame ride on the frame, so the GL renderer uploads only those with
 * `bufferSubData`. `checksum` hashes the instances; an incremental pack and
 * a pack from scratch of the same scene must agree on it.
 *
 * Pure: no DOM, no GL. The conductor supplies what is "shown" (the store
 * with not-yet-sealed flips undone) and the per-cell animations.
 */

import type { FieldLayout, FrameModel, PlaneId, PositionMatter, Scene, WorldMatter } from '../contracts';
import { DARK_THEME, desaturate, occupierRgb, type Rgb, type Theme } from './palette';
import { DEFAULT_MOTION, progress } from './tween';

// ---------- instance layout ----------

/** Offsets inside one bit instance (FrameModel.bits: 12 floats each). */
export const BIT = { x: 0, y: 1, w: 2, h: 3, r: 4, g: 5, b: 6, a: 7, glow: 8, state: 9, phase: 10, stack: 11, floats: 12 } as const;

/** The low four bits of `state`: which shape the shader draws (§4.1). */
export const SHAPE = {
  free: 0,        // 1.5 px ring on the free fill, ≥ 3:1
  held: 1,        // solid, occupier hue
  intent: 2,      // hollow ring with a 45° hatch
  proposed: 3,    // amber dashed outline
  foreign: 4,     // grey dotted outline: busy there
  shield: 5,      // refused: red hex ring and ✕, never a fill
  aim: 6,         // a peer aiming here: ring in their viewer hue
  select: 7,      // the selection: focus outline
  hint: 8,        // a local preview: dashed, ok / maybe / no
  pending: 9,     // at the door: a shimmer, motion not matter
  dot: 10,        // a lane's empty cell
  highlight: 11,  // reduced motion: the 1 px outline that lingers a second
  plate: 12,      // the field plate under a World
} as const;
/** Flags above the shape. */
export const FLAG = { pinned: 16, lane: 32 } as const;

export const shapeOf = (state: number): number => state & 15;

/** Seat pips ride in `stack`: filled count, plus 256 × seats (pips only when seats > 1). */
export const packStack = (count: number, seats: number): number => (seats > 1 ? Math.min(count, 255) + 256 * Math.min(seats, 64) : 0);
export const unpackStack = (stack: number): { count: number; seats: number } => ({ count: stack % 256, seats: Math.floor(stack / 256) });

// ---------- layout helpers (shared with hit-testing) ----------

/**
 * Row and lane `y` values in a FieldLayout are offsets from `layout.y`, so a
 * World moves by changing its x and y alone. These helpers are the one place
 * that reads them; hit-testing should use them too.
 */
export const rowTop = (layout: FieldLayout, rowY: number): number => layout.y + rowY;
export const pitch = (layout: FieldLayout): number => layout.cell + layout.gap;
export const cellLeft = (layout: FieldLayout, slot: number): number => layout.x + slot * pitch(layout);
export const fieldWidth = (layout: FieldLayout): number => layout.slots * pitch(layout) - layout.gap;

/** Which cell a stage point falls in (gaps count to the cell on their left and above), or null. */
export const cellAt = (layout: FieldLayout, sx: number, sy: number): { key: number; slot: number; plane: PlaneId | null } | null => {
  const p = pitch(layout);
  const slot = Math.floor((sx - layout.x) / p);
  if (slot < 0 || slot >= layout.slots) return null;
  for (const r of layout.rows) {
    const top = rowTop(layout, r.y);
    if (sy >= top && sy < top + p) return { key: r.key, slot, plane: null };
  }
  for (const l of layout.lanes) {
    const top = rowTop(layout, l.y);
    if (sy >= top && sy < top + p) return { key: l.key, slot, plane: l.plane };
  }
  return null;
};

// ---------- masks ----------

/** A mask as 32-bit words, low slot first. */
export const maskWords = (mask: bigint, slots: number, out?: Uint32Array): Uint32Array => {
  const n = Math.max(1, Math.ceil(slots / 32));
  const w = out && out.length >= n ? out : new Uint32Array(n);
  let m = mask < 0n ? 0n : mask;
  for (let i = 0; i < n; i++) {
    w[i] = Number(m & 0xffffffffn);
    m >>= 32n;
  }
  return w;
};
export const bitOf = (w: Uint32Array, s: number): boolean => (((w[s >>> 5] ?? 0) >>> (s & 31)) & 1) === 1;

export const slotsOfMask = (mask: bigint, limit = 4096): number[] => {
  const out: number[] = [];
  let m = mask, s = 0;
  while (m > 0n && s < limit) {
    if (m & 1n) out.push(s);
    m >>= 1n;
    s++;
  }
  return out;
};

const ROLE_ORDER: Record<string, number> = { held: 0, intent: 1, proposed: 2 };
export const parsePlane = (p: PlaneId): { id: number; role: 'held' | 'intent' | 'proposed' } => {
  const bar = p.indexOf('|');
  return { id: Number(p.slice(0, bar)), role: p.slice(bar + 1) as 'held' | 'intent' | 'proposed' };
};
const planeOrder = (a: PlaneId, b: PlaneId): number => {
  const pa = parsePlane(a), pb = parsePlane(b);
  return pa.id - pb.id || (ROLE_ORDER[pa.role] ?? 3) - (ROLE_ORDER[pb.role] ?? 3);
};

// ---------- hashing ----------

const FNV = 0x811c9dc5;
const mixH = (h: number, v: number): number => Math.imul(h ^ (v | 0), 0x01000193);
const mixF = (h: number, f: number): number => mixH(h, Math.round(f * 1024));
const mixS = (h: number, s: string): number => {
  for (let i = 0; i < s.length; i++) h = mixH(h, s.charCodeAt(i));
  return h;
};

/** FNV-1a over the first `count` instances' raw floats: the instance checksum. */
export const checksum = (bits: Float32Array, count: number, floats: number = BIT.floats): number => {
  const u = new Uint32Array(bits.buffer, bits.byteOffset, count * floats);
  let h = FNV;
  for (let i = 0; i < u.length; i++) h = mixH(h, u[i] as number);
  return h >>> 0;
};

// ---------- inputs from the conductor ----------

/** The scene fx is handed: the contract's Scene (pinned and occupierId included). */
export type FxScene = Scene;

/** One cell animation on a row: which slots, when each starts, and how they looked before. */
export interface CellAnim {
  readonly kind: 'ignite' | 'fade' | 'correct';
  readonly cells: Uint8Array;            // 1 where it applies, by slot
  readonly delays: Float32Array;         // ms after t0, by slot
  readonly t0: number;
  readonly dur: number;
  readonly before: Float32Array | null;  // the cells as drawn before, 12 floats per slot
}

/** A drawn-over shape on some slots of a position (shield, aim, pending shimmer, highlight). */
export interface Decoration {
  readonly world: string; readonly key: number; readonly slots: bigint;
  readonly shape: number; readonly rgb: Rgb; readonly alpha: number; readonly glow: number; readonly phase: number;
  readonly inflate?: number;   // grow the cell by this many stage units each side
}

export type Shown = (world: string, key: number, plane: PlaneId, stored: bigint) => bigint;
/**
 * Planes still shown at a position that the store may no longer hold: a
 * store deletes a plane whose mask reaches 0, but until that flip's seal has
 * played the plane is still on show. Duplicates and planes the store holds
 * are fine; the builder takes the union.
 */
export type ShownPlanes = (world: string, key: number) => Iterable<PlaneId> | undefined;

/** What a new scene did to the rows: nothing moved, some moved or resized, or the row table was rebuilt. */
export type SceneChange = 'same' | 'moved' | 'rebuilt';

// ---------- the builder ----------

interface Row {
  readonly id: string;
  readonly world: string;
  readonly key: number;
  readonly plane: PlaneId | null;
  layout: FieldLayout;       // the latest layout object: geometry may move under the same structure
  y: number;                 // absolute stage y of the row's top
  readonly index: number;    // row order within its layout (for stagger)
  readonly depth: number;    // lane order, for the exploded view
  readonly offset: number;
  sig: number;
}

export type Ranges = [number, number][];

export interface PackedFrame extends FrameModel {
  /** Instance ranges [from, to) rewritten by the pack that made this frame. */
  readonly dirty: Ranges;
  /** One more than the previous pack's: a renderer that missed one uploads everything. */
  readonly version: number;
  /** Where rows end and decorations begin. */
  readonly rowsEnd: number;
}

export const rowId = (world: string, key: number, plane: PlaneId | null = null): string =>
  plane === null ? `${world}|${key}` : `${world}|${key}|${plane}`;

const PLATE_PAD = 10;

export class FrameBuilder {
  bits = new Float32Array(BIT.floats * 256);
  bitCount = 0;
  private rows: Row[] = [];
  private byId = new Map<string, Row>();
  private byPos = new Map<string, Row[]>();   // world|key → the main row and its lanes
  private structure = '';
  private scene: FxScene | null = null;
  private theme: Theme = DARK_THEME;
  private themeSig = 0;
  private shown: Shown = (_w, _k, _p, m) => m;
  private extra: ShownPlanes = () => undefined;
  private force = new Set<string>();
  private check = false;
  private anims = new Map<string, CellAnim[]>();
  private dims = new Map<string, number>();
  private decorations: readonly Decoration[] = [];
  private decoSig = -1;
  private plates = 0;
  private rowsEnd = 0;
  private dirty: Ranges = [];
  private version = 0;
  private words: Uint32Array[] = [];

  setTheme(theme: Theme): void {
    const sig = mixS(FNV, JSON.stringify([theme.dark, theme.freeRing, theme.ask, theme.foreign, theme.occL, theme.occC, theme.mine, theme.refuse, theme.focus, theme.bg2]));
    if (sig === this.themeSig) return;
    this.theme = theme;
    this.themeSig = sig;
    for (const r of this.rows) this.force.add(r.id);
    this.decoSig = -1;
  }

  /** What is shown: the store with unplayed flips undone, over the store's planes and any `planes` still names. */
  setShown(fn: Shown, planes?: ShownPlanes): void {
    this.shown = fn;
    this.extra = planes ?? (() => undefined);
  }

  /** A new scene: rebuild the row table if the layouts changed shape, else check every row's signature at the next pack. */
  setScene(scene: FxScene): SceneChange {
    const had = this.rows.length > 0;
    this.scene = scene;
    const structure = scene.layouts.map((l) =>
      `${l.world}:${l.slots}:${l.rows.map((r) => r.key).join(',')}:${l.lanes.map((n) => `${n.key}/${n.plane}`).join(',')}`).join(';');
    let change: SceneChange = 'same';
    if (structure !== this.structure) {
      this.structure = structure;
      this.layoutRows(scene);
      change = had || this.rows.length > 0 ? 'rebuilt' : 'same';
    } else {
      // same rows in the same order: take the new geometry, and let the signatures decide what moved
      let i = 0, moved = false;
      for (const layout of scene.layouts) {
        for (const r of layout.rows) if (this.relayout(i++, layout, r.y)) moved = true;
        for (const l of layout.lanes) if (this.relayout(i++, layout, l.y)) moved = true;
      }
      if (moved) change = 'moved';
    }
    this.check = true;
    this.decoSig = -1;
    return change;
  }

  /** Repack a position's rows at the next pack (a flip landed there). */
  invalidate(world: string, key?: number): void {
    if (key === undefined) {
      for (const r of this.rows) if (r.world === world) this.force.add(r.id);
      return;
    }
    for (const r of this.byPos.get(`${world}|${key}`) ?? []) this.force.add(r.id);
  }

  invalidateAll(): void {
    for (const r of this.rows) this.force.add(r.id);
    this.decoSig = -1;
  }

  /** Recheck every row's signature at the next pack (the store may have moved under us). */
  recheck(): void {
    this.check = true;
  }

  /** The rows drawn for a position: its main row first, then its lanes. */
  rowsAt(world: string, key: number): readonly { id: string; plane: PlaneId | null; index: number; layout: FieldLayout; y: number }[] {
    return this.byPos.get(`${world}|${key}`) ?? [];
  }

  hasRow(id: string): boolean {
    return this.byId.has(id);
  }

  /** A copy of a row's instances as last packed (what the eye sees now). */
  capture(id: string): Float32Array | null {
    const r = this.byId.get(id);
    if (!r) return null;
    const from = r.offset * BIT.floats;
    return this.bits.slice(from, from + r.layout.slots * BIT.floats);
  }

  animate(id: string, anim: CellAnim): void {
    if (!this.byId.has(id)) return;
    const list = this.anims.get(id);
    if (list) list.push(anim); else this.anims.set(id, [anim]);
    this.force.add(id);
  }

  animating(now: number): boolean {
    for (const list of this.anims.values()) for (const a of list) if (!this.animDone(a, now)) return true;
    return false;
  }

  /** Desaturate a World toward grey (0..1 of the way to 70 %): objects a flight cannot land on. */
  setDim(world: string, amount: number): void {
    const q = Math.round(amount * 20) / 20;
    if ((this.dims.get(world) ?? 0) === q) return;
    if (q === 0) this.dims.delete(world); else this.dims.set(world, q);
    this.invalidate(world);
  }

  setDecorations(list: readonly Decoration[]): void {
    this.decorations = list;
  }

  /** Where a cell sits, in stage units: the main row's cell of (world, key, slot). */
  cellRect(world: string, key: number, slot: number): { x: number; y: number; w: number; h: number } | null {
    const r = this.byPos.get(`${world}|${key}`)?.[0];
    if (!r) return null;
    return { x: cellLeft(r.layout, slot), y: r.y, w: r.layout.cell, h: r.layout.cell };
  }

  /** The instances, with only what changed repacked. */
  pack(now: number, frame: { particles: Float32Array; particleCount: number; ripples: Float32Array; rippleCount: number; camera: Float32Array; overlay: FrameModel['overlay'] }): PackedFrame {
    this.dirty = [];
    const scene = this.scene;
    if (scene) {
      if (this.check) {
        this.check = false;
        for (const r of this.rows) {
          const sig = this.signature(r, scene);
          if (sig !== r.sig) this.force.add(r.id);
        }
      }
      for (const id of this.anims.keys()) this.force.add(id);
      if (this.plates > 0 && this.force.size > 0) this.packPlates(scene);
      for (const id of this.force) {
        const r = this.byId.get(id);
        if (!r) continue;
        r.sig = this.signature(r, scene);
        this.packRow(r, scene, now);
        this.mark(r.offset, r.offset + r.layout.slots);
      }
      this.force.clear();
      for (const [id, list] of this.anims) {
        const live = list.filter((a) => !this.animDone(a, now));
        if (live.length === 0) {
          this.anims.delete(id);
          this.force.add(id);     // one more pack to land on the settled look
        } else if (live.length !== list.length) this.anims.set(id, live);
      }
      this.packDecorations(scene);
    }
    this.settleDirty();
    this.version++;
    return {
      bits: this.bits, bitCount: this.bitCount,
      particles: frame.particles, particleCount: frame.particleCount,
      ripples: frame.ripples, rippleCount: frame.rippleCount,
      camera: frame.camera, overlay: frame.overlay,
      dirty: this.dirty, version: this.version, rowsEnd: this.rowsEnd,
    };
  }


  // ---------- internals ----------

  private animDone(a: CellAnim, now: number): boolean {
    let maxDelay = 0;
    for (let s = 0; s < a.cells.length; s++) if (a.cells[s] && (a.delays[s] as number) > maxDelay) maxDelay = a.delays[s] as number;
    return now >= a.t0 + maxDelay + a.dur;
  }

  private mark(from: number, to: number): void {
    if (to > from) this.dirty.push([from, to]);
  }

  /** Sort and merge the ranges a pack touched. */
  private settleDirty(): void {
    if (this.dirty.length < 2) return;
    this.dirty.sort((a, b) => a[0] - b[0]);
    const out: Ranges = [];
    for (const r of this.dirty) {
      const last = out[out.length - 1];
      if (last && r[0] <= last[1]) last[1] = Math.max(last[1], r[1]);
      else out.push([r[0], r[1]]);
    }
    this.dirty = out;
  }

  private ensure(instances: number): void {
    const need = instances * BIT.floats;
    if (this.bits.length >= need) return;
    let cap = this.bits.length;
    while (cap < need) cap *= 2;
    const next = new Float32Array(cap);
    next.set(this.bits);
    this.bits = next;
  }

  private layoutRows(scene: FxScene): void {
    this.rows = [];
    this.byId.clear();
    this.byPos.clear();
    this.plates = scene.layouts.length;
    let offset = this.plates;
    for (const layout of scene.layouts) {
      layout.rows.forEach((r, index) => {
        const row: Row = { id: rowId(layout.world, r.key), world: layout.world, key: r.key, plane: null, layout, y: rowTop(layout, r.y), index, depth: 0, offset, sig: 0 };
        offset += layout.slots;
        this.addRow(row);
      });
      layout.lanes.forEach((l, depth) => {
        const main = this.byPos.get(`${layout.world}|${l.key}`)?.[0];
        const row: Row = { id: rowId(layout.world, l.key, l.plane), world: layout.world, key: l.key, plane: l.plane, layout, y: rowTop(layout, l.y), index: main?.index ?? 0, depth: depth + 1, offset, sig: 0 };
        offset += layout.slots;
        this.addRow(row);
      });
    }
    this.rowsEnd = offset;
    this.ensure(offset);
    this.bitCount = offset;
    for (const id of [...this.anims.keys()]) if (!this.byId.has(id)) this.anims.delete(id);
    for (const r of this.rows) this.force.add(r.id);
    this.decoSig = -1;
  }

  /** Take a row's new geometry; true when it moved or resized. */
  private relayout(i: number, layout: FieldLayout, y: number): boolean {
    const row = this.rows[i];
    if (!row) return false;
    const was = row.layout, top = rowTop(layout, y);
    const moved = was.x !== layout.x || was.cell !== layout.cell || was.gap !== layout.gap || was.exploded !== layout.exploded || row.y !== top;
    row.layout = layout;
    row.y = top;
    return moved;
  }

  private addRow(row: Row): void {
    this.rows.push(row);
    this.byId.set(row.id, row);
    const k = `${row.world}|${row.key}`;
    const list = this.byPos.get(k);
    if (list) list.push(row); else this.byPos.set(k, [row]);
  }

  private matter(scene: FxScene, world: string): WorldMatter | undefined {
    return scene.matter(world);
  }

  /**
   * The planes shown at a position: the store's, then any it has dropped
   * that unplayed flips still show (a full release, a handover's from-plane).
   * Null when there are none of the latter, so the common case allocates nothing.
   */
  private dropped(r: Row, pm: PositionMatter): PlaneId[] | null {
    const extra = this.extra(r.world, r.key);
    if (!extra) return null;
    let out: PlaneId[] | null = null;
    for (const plane of extra) {
      if (pm.planes.has(plane) || out?.includes(plane)) continue;
      (out ??= []).push(plane);
    }
    return out;
  }

  private signature(r: Row, scene: FxScene): number {
    let h = mixS(FNV, r.id);
    h = mixF(h, r.layout.x); h = mixF(h, r.y); h = mixF(h, r.layout.cell); h = mixF(h, r.layout.gap);
    h = mixH(h, r.layout.slots); h = mixH(h, this.themeSig);
    h = mixF(h, this.dims.get(r.world) ?? 0);
    h = mixH(h, r.layout.exploded ? 1 : 0);
    const wm = this.matter(scene, r.world);
    const pm = wm?.byKey.get(r.key);
    if (!wm || !pm) return h >>> 0;
    h = mixH(h, wm.seats);
    const planeSig = (plane: PlaneId, stored: bigint): void => {
      if (r.plane !== null && plane !== r.plane) return;
      const m = this.shown(r.world, r.key, plane, stored);
      if (m === 0n && r.plane === null) return;
      const { id } = parsePlane(plane);
      // order-independent: planes are summed, so the store's map order does not matter
      let ph = mixS(FNV, plane);
      const w = maskWords(m, r.layout.slots);
      for (let i = 0; i < w.length; i++) ph = mixH(ph, w[i] as number);
      ph = mixF(ph, scene.hue(r.world, plane));
      ph = mixH(ph, scene.pinned?.(r.world, id) ? 1 : 0);
      ph = mixS(ph, wm.kinds.get(id) ?? '?');
      h = (h + (ph >>> 0)) | 0;
    };
    for (const [plane, stored] of pm.planes) planeSig(plane, stored);
    for (const plane of this.dropped(r, pm) ?? []) planeSig(plane, 0n);
    return h >>> 0;
  }

  private write(i: number, x: number, y: number, w: number, hh: number, c: Rgb, a: number, glow: number, state: number, phase: number, stack: number): void {
    const o = i * BIT.floats, b = this.bits;
    b[o] = x; b[o + 1] = y; b[o + 2] = w; b[o + 3] = hh;
    b[o + 4] = c[0]; b[o + 5] = c[1]; b[o + 6] = c[2]; b[o + 7] = a;
    b[o + 8] = glow; b[o + 9] = state; b[o + 10] = phase; b[o + 11] = stack;
  }

  private packPlates(scene: FxScene): void {
    const t = this.theme;
    const plate: Rgb = t.dark ? [t.bg2[0] + 0.02, t.bg2[1] + 0.025, t.bg2[2] + 0.03] : [1, 1, 1];
    let changed = false;
    scene.layouts.forEach((l, i) => {
      const ys = [...l.rows.map((r) => r.y), ...l.lanes.map((n) => n.y)];
      const top = ys.length ? Math.min(...ys) : 0, bottom = ys.length ? Math.max(...ys) + l.cell : l.cell;
      const o = i * BIT.floats;
      const x = l.x - PLATE_PAD, y = rowTop(l, top) - PLATE_PAD, w = fieldWidth(l) + 2 * PLATE_PAD, hh = bottom - top + 2 * PLATE_PAD;
      if (this.bits[o] !== Math.fround(x) || this.bits[o + 1] !== Math.fround(y) || this.bits[o + 2] !== Math.fround(w) || this.bits[o + 3] !== Math.fround(hh) || this.bits[o + 4] !== Math.fround(plate[0])) changed = true;
      this.write(i, x, y, w, hh, plate, t.dark ? 0.55 : 0.6, 0, SHAPE.plate, 0, 0);
    });
    if (changed) this.mark(0, this.plates);
  }

  private packRow(r: Row, scene: FxScene, now: number): void {
    const t = this.theme;
    const L = r.layout;
    const slots = L.slots;
    const wm = this.matter(scene, r.world);
    const pm = wm?.byKey.get(r.key);
    const dim = this.dims.get(r.world) ?? 0;
    const tint = (c: Rgb): Rgb => (dim > 0 ? desaturate(c, 0.7 * dim) : c);
    const seats = wm?.seats ?? 1;

    // Planes in a stable order, each as words.
    const planes: { plane: PlaneId; id: number; role: string; foreign: boolean; rgb: Rgb; pinned: boolean }[] = [];
    let n = 0;
    if (wm && pm) {
      const ids = [...pm.planes.keys()];
      const dropped = this.dropped(r, pm);
      if (dropped) ids.push(...dropped);
      ids.sort(planeOrder);
      for (const plane of ids) {
        if (r.plane !== null && plane !== r.plane) continue;
        const m = this.shown(r.world, r.key, plane, pm.planes.get(plane) ?? 0n);
        if (m === 0n && r.plane === null) continue;
        const { id, role } = parsePlane(plane);
        const foreign = wm.kinds.get(id) === 'foreign';
        const rgb = role === 'proposed' ? t.ask : foreign ? t.foreign : occupierRgb(t, scene.hue(r.world, plane));
        this.words[n] = maskWords(m, slots, this.words[n]);
        planes[n] = { plane, id, role, foreign, rgb: tint(rgb), pinned: scene.pinned?.(r.world, id) ?? false };
        n++;
      }
    }
    const lane = r.plane !== null;
    const freeRing = tint(t.freeRing);
    const anims = this.anims.get(r.id);

    for (let s = 0; s < slots; s++) {
      const x = cellLeft(L, s), y = r.y, c = L.cell;
      let shape: number = lane ? SHAPE.dot : SHAPE.free;
      let rgb = freeRing;
      let glow = 0, count = 0, flags = lane ? FLAG.lane : 0;
      let rank = 9;
      for (let i = 0; i < n; i++) {
        const p = planes[i]!;
        if (!bitOf(this.words[i]!, s)) continue;
        // held beats intent beats proposed beats a foreign copy; the lowest id wins a tie
        // an ask on a hand is a proposed plane under the asker's foreign record: it stays an ask
        const pr = p.role === 'proposed' ? 2 : p.foreign ? 3 : p.role === 'held' ? 0 : 1;
        if (p.role === 'held' && !p.foreign) count++;
        if (pr < rank) {
          rank = pr;
          rgb = p.rgb;
          shape = pr === 0 ? SHAPE.held : pr === 1 ? SHAPE.intent : pr === 2 ? SHAPE.proposed : SHAPE.foreign;
          // A held run lights the plate around it (the glow pass, additive on dark): the runs are the hero, not the grid.
          glow = pr === 0 ? 0.62 : pr === 1 ? 0.2 : 0;
          flags = (lane ? FLAG.lane : 0) | (p.pinned && pr === 0 ? FLAG.pinned : 0);
        }
      }
      const alpha = lane && shape === SHAPE.dot ? 0.55 : r.depth > 0 && L.exploded ? Math.max(0.55, 1 - r.depth * 0.06) : 1;
      this.write(r.offset + s, x, y, c, c, rgb, alpha, glow, shape | flags, 0, lane ? 0 : packStack(count, seats));
    }

    if (anims) for (const a of anims) this.applyAnim(r, a, now);
  }

  private applyAnim(r: Row, a: CellAnim, now: number): void {
    const b = this.bits;
    for (let s = 0; s < r.layout.slots && s < a.cells.length; s++) {
      if (!a.cells[s]) continue;
      const o = (r.offset + s) * BIT.floats;
      const start = a.t0 + (a.delays[s] as number);
      const p = progress(now, start, a.dur);
      if (now < start && a.before) {
        // not yet reached by the stagger: still as it was
        b.set(a.before.subarray(s * BIT.floats, (s + 1) * BIT.floats), o);
        continue;
      }
      if (p >= 1) continue;
      if (a.kind === 'ignite') {
        const e = DEFAULT_MOTION.out(p);
        const k = 0.6 + 0.4 * e;
        const w = b[o + 2] as number, h = b[o + 3] as number;
        b[o] = (b[o] as number) + (w * (1 - k)) / 2;
        b[o + 1] = (b[o + 1] as number) + (h * (1 - k)) / 2;
        b[o + 2] = w * k; b[o + 3] = h * k;
        b[o + 8] = Math.max(b[o + 8] as number, 1 - p);
        b[o + 10] = p;
      } else if (a.kind === 'fade') {
        // a crossfade in one instance: the old look goes out, then the new one comes up
        if (p < 0.5 && a.before) {
          b.set(a.before.subarray(s * BIT.floats, (s + 1) * BIT.floats), o);
          b[o + 7] = (b[o + 7] as number) * (1 - 2 * p);
        } else {
          b[o + 7] = (b[o + 7] as number) * (a.before ? 2 * p - 1 : p);
        }
        b[o + 10] = p;
      } else {
        // a correction: the old look shimmers for the first half, then the new one settles
        if (p < 0.5 && a.before) {
          b.set(a.before.subarray(s * BIT.floats, (s + 1) * BIT.floats), o);
          b[o + 8] = 0.6 * Math.sin(p * Math.PI * 6) ** 2;
        } else {
          b[o + 8] = Math.max(b[o + 8] as number, 1 - p);
        }
        b[o + 10] = p;
      }
    }
  }

  private packDecorations(scene: FxScene): void {
    const t = this.theme;
    const items: Decoration[] = [];
    for (const sel of scene.selection) items.push({ world: sel.world, key: sel.key, slots: sel.slots, shape: SHAPE.select, rgb: t.focus, alpha: 1, glow: 0.15, phase: 0 });
    for (const hint of scene.hints) {
      const rgb = hint.verdict === 'ok' ? t.mine : hint.verdict === 'maybe' ? t.ask : t.refuse;
      items.push({ world: hint.world, key: hint.key, slots: hint.slots, shape: SHAPE.hint, rgb, alpha: 0.9, glow: 0, phase: 0 });
    }
    for (const d of this.decorations) items.push(d);

    let sig = mixH(FNV, items.length);
    for (const d of items) {
      sig = mixS(sig, d.world); sig = mixH(sig, d.key); sig = mixS(sig, d.slots.toString(16));
      sig = mixH(sig, d.shape); sig = mixF(sig, d.alpha); sig = mixF(sig, d.glow); sig = mixF(sig, d.phase);
      sig = mixF(sig, d.rgb[0]); sig = mixF(sig, d.rgb[1]); sig = mixF(sig, d.rgb[2]); sig = mixF(sig, d.inflate ?? 0);
    }
    sig = mixH(sig, this.rowsEnd);
    if (sig === this.decoSig) return;
    this.decoSig = sig;

    let i = this.rowsEnd;
    for (const d of items) {
      const row = this.byPos.get(`${d.world}|${d.key}`)?.[0];
      if (!row) continue;
      const L = row.layout;
      const inflate = d.inflate ?? 0;
      for (const s of slotsOfMask(d.slots, L.slots)) {
        this.ensure(i + 1);
        const c = L.cell + 2 * inflate;
        this.write(i, cellLeft(L, s) - inflate, row.y - inflate, c, c, d.rgb, d.alpha, d.glow, d.shape, d.phase, 0);
        i++;
      }
    }
    const before = this.bitCount;
    this.bitCount = i;
    this.mark(this.rowsEnd, Math.max(i, before));
  }
}

/** One-shot: pack a scene from scratch (tests, and the checksum's reference). */
export const packScene = (scene: FxScene, theme: Theme, now = 0, shown?: Shown, decorations: readonly Decoration[] = []): { bits: Float32Array; count: number; sum: number } => {
  const b = new FrameBuilder();
  b.setTheme(theme);
  if (shown) b.setShown(shown);
  b.setScene(scene);
  b.setDecorations(decorations);
  const f = b.pack(now, { particles: new Float32Array(0), particleCount: 0, ripples: new Float32Array(0), rippleCount: 0, camera: new Float32Array(0), overlay: [] });
  return { bits: f.bits, count: f.bitCount, sum: checksum(f.bits, f.bitCount) };
};
